import { useEffect, useState } from 'react';
import { matchPlaceRule, suggestPhrase } from '@app/shared/place-rules';
import { useCompanySettings } from '../settings/company';
import { EMPTY_PLACE, fullPlace, usePlaceRules, type PlaceRule, type PlaceValue } from '../settings/placeDefaults';

export type { PlaceValue };

/**
 * The place a new event comes with (owner 2026-10-30 22:20 / 22:23): the place of the rule whose phrase is in the
 * name („PFK” → Sopot, Mamuszki 14), else the default place of the company (Gdańsk). Only a prefill: a place typed or
 * chosen by hand is never overwritten.
 *
 * - `none`: nothing filled yet (the form waits for the settings and the rules);
 * - `auto`: the form holds `place`, filled from `source` — a new name may change it;
 * - `manual`: the person changed the place (or it came with the draft): the form's place is theirs.
 */
export type AutoSource = { kind: 'rule'; phrase: string } | { kind: 'default' };
export type AutoState = { kind: 'none' } | { kind: 'auto'; place: PlaceValue; source: AutoSource } | { kind: 'manual' };
export type Wanted = { place: PlaceValue; source: AutoSource } | null;

const json = (v: unknown) => JSON.stringify(v);
const isEmpty = (p: PlaceValue) => p.location.trim() === '' && p.locationAddress.trim() === '' && p.locationPoint === null;
/** The same place: name, address, point and ids ("Lokalizacja nieprecyzyjna" aside). */
const samePlace = (a: PlaceValue, b: PlaceValue) =>
  a.location.trim() === b.location.trim() && a.locationAddress.trim() === b.locationAddress.trim() && json(a.locationPoint) === json(b.locationPoint) && (a.venueId ?? null) === (b.venueId ?? null) && (a.googlePlaceId ?? '') === (b.googlePlaceId ?? '');
const exactly = (a: PlaceValue, b: PlaceValue) => samePlace(a, b) && a.locationApprox === b.locationApprox && a.location === b.location && a.locationAddress === b.locationAddress;

/** What the name asks for: the rule's place (the longest phrase), else the default place, else nothing. */
export function wantedPlace(name: string, rules: readonly PlaceRule[], defaultPlace: Partial<PlaceValue> | null | undefined): Wanted {
  const rule = matchPlaceRule(name, rules);
  if (rule) return { place: fullPlace(rule.place), source: { kind: 'rule', phrase: rule.phrase } };
  const def = defaultPlace ? fullPlace(defaultPlace) : null;
  return def && !isEmpty(def) ? { place: def, source: { kind: 'default' } } : null;
}

/** One step: the form's place now, what was filled before and what the name asks for → the place to put (null: keep) and the new state. */
export function autoPlaceStep(current: PlaceValue, state: AutoState, wanted: Wanted): { place: PlaceValue | null; state: AutoState } {
  if (state.kind === 'manual') return { place: null, state };
  if (state.kind === 'none') {
    if (!isEmpty(current)) return { place: null, state: { kind: 'manual' } };
    return wanted ? { place: wanted.place, state: { kind: 'auto', ...wanted } } : { place: null, state };
  }
  if (exactly(current, state.place)) {
    if (wanted && exactly(wanted.place, state.place)) return { place: null, state: json(wanted.source) === json(state.source) ? state : { kind: 'auto', ...wanted } };
    return wanted ? { place: wanted.place, state: { kind: 'auto', ...wanted } } : { place: EMPTY_PLACE, state: { kind: 'none' } };
  }
  // changed by hand. Only the name or the address typed (point and ids still the filled ones): another place — the
  // rest of the filled one goes, only what was typed stays
  const filled = state.place;
  const nameTyped = current.location !== filled.location;
  const addressTyped = current.locationAddress !== filled.locationAddress;
  const textsOnly = (nameTyped || addressTyped) && json(current.locationPoint) === json(filled.locationPoint) && current.venueId === filled.venueId && current.googlePlaceId === filled.googlePlaceId;
  if (!textsOnly) return { place: null, state: { kind: 'manual' } };
  const next: PlaceValue = { ...EMPTY_PLACE, location: nameTyped ? current.location : '', locationAddress: addressTyped ? current.locationAddress : '' };
  return { place: exactly(next, current) ? null : next, state: { kind: 'manual' } };
}

/**
 * „Zapamiętać: „PFK” → …?” after a new event is saved: its place was set by hand and is not what the rules gave. The
 * phrase: the matched rule's (saving changes that rule), else the name's first acronym or first word. Null: nothing to ask.
 */
export function rememberOffer(name: string, place: PlaceValue, state: AutoState, rules: readonly PlaceRule[]): { phrase: string; place: PlaceValue } | null {
  if (isEmpty(place)) return null;
  if (state.kind === 'auto' && exactly(place, state.place)) return null;
  const rule = matchPlaceRule(name, rules);
  if (rule && samePlace(fullPlace(rule.place), place)) return null;
  const phrase = rule?.phrase ?? suggestPhrase(name);
  return phrase.trim() === '' ? null : { phrase, place };
}

/** The place of a form's draft. */
export const placeOfDraft = (d: { location: string; locationAddress: string; locationPoint: PlaceValue['locationPoint']; locationApprox: boolean; venueId?: string | null | undefined; googlePlaceId?: string | undefined }): PlaceValue => ({
  location: d.location,
  locationAddress: d.locationAddress,
  locationPoint: d.locationPoint,
  locationApprox: d.locationApprox,
  venueId: d.venueId ?? null,
  googlePlaceId: d.googlePlaceId ?? '',
});

/** What a draft takes from a filled place: the place, and no notes, place point or route of the one before. */
export const draftPatchOf = (p: PlaceValue) => ({ ...p, placeNotes: '', placePoint: p.venueId || p.googlePlaceId ? p.locationPoint : null, chosenRoute: null });

/**
 * The draft with the filled place — only while its place is still `from`, the one the step was worked out for: a key
 * typed in between (before the effect ran) wins, the fill is dropped.
 */
export function fillIfUnchanged<T extends Parameters<typeof placeOfDraft>[0]>(draft: T, from: PlaceValue, next: PlaceValue): T {
  return json(placeOfDraft(draft)) === json(from) ? { ...draft, ...draftPatchOf(next) } : draft;
}

/** The hint under the place while it is the filled one. */
export function autoPlaceHint(state: AutoState, current: PlaceValue): string | null {
  if (state.kind !== 'auto' || !exactly(current, state.place)) return null;
  return state.source.kind === 'rule' ? `Miejsce z reguły „${state.source.phrase}” — zmień w Ustawieniach` : 'Domyślne miejsce nowego wydarzenia — zmień w Ustawieniach';
}

/** Whether the form's place is still the filled one (then it is no change of the person: no „Masz niezapisane zmiany”). */
export const isAutoFilled = (state: AutoState, current: PlaceValue): boolean => state.kind === 'auto' && exactly(current, state.place);

/**
 * The prefill in a new-event form: once the settings and the rules are read (or could not be), and again whenever the
 * name or the place changes. `enabled` false (an edit, or a role that does not see the place): nothing happens.
 * `apply(place, from)`: put `place` into the draft only while its place is still `from` (`fillIfUnchanged`).
 */
export function useAutoPlace(name: string, current: PlaceValue, apply: (place: PlaceValue, from: PlaceValue) => void, enabled: boolean): { state: AutoState; rules: PlaceRule[] } {
  const settings = useCompanySettings(enabled);
  const rulesQuery = usePlaceRules(enabled);
  const ready = enabled && (settings.isFetched || settings.isError) && (rulesQuery.isFetched || rulesQuery.isError);
  const rules = rulesQuery.data?.rules ?? NO_RULES;
  const defaultPlace = settings.data?.defaultPlace ?? null;
  const [state, setState] = useState<AutoState>({ kind: 'none' });
  const currentKey = json(current);
  useEffect(() => {
    if (!ready) return;
    const from = JSON.parse(currentKey) as PlaceValue;
    const step = autoPlaceStep(from, state, wantedPlace(name, rules, defaultPlace));
    if (step.place) apply(step.place, from);
    if (json(step.state) !== json(state)) setState(step.state);
    // `apply` is the form's setter: a new function each render, the same effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, name, currentKey, state, rules, defaultPlace]);
  return { state, rules };
}

const NO_RULES: PlaceRule[] = [];

/** The place fields of the form as it opened (to tell a filled place from a change of the person). */
export const freshPlaceOf = <T extends { location: string; locationAddress: string; locationPoint: PlaceValue['locationPoint']; locationApprox: boolean; venueId?: string | null | undefined; googlePlaceId?: string | undefined; placeNotes?: string | undefined; placePoint?: PlaceValue['locationPoint'] | undefined; chosenRoute?: unknown }>(d: T) => ({
  location: d.location,
  locationAddress: d.locationAddress,
  locationPoint: d.locationPoint,
  locationApprox: d.locationApprox,
  venueId: d.venueId,
  googlePlaceId: d.googlePlaceId,
  placeNotes: d.placeNotes,
  placePoint: d.placePoint,
  chosenRoute: d.chosenRoute,
});
